import React from 'react';
import PropTypes from 'prop-types';
import { Form, Field, useFormikContext } from 'formik';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { validateEmails } from '../../../i18n/common';
import { FormikChips, FormikMultiSelect, FormikCheckbox } from '../../../components/prime-formik';
import { renderAsyncSelector } from '../../../components/formik-components/FormAsyncSelector';
import FormLanguageSelector from '../../../components/formik-components/FormLanguageSelector';
import Debug from '../../../components/Debug';
import useCustomFunctionalityCheck from '../../../hooks/useCustomFunctionalityCheck';

const i18n = defineMessages({
  choose: {
    id: 'choose',
    defaultMessage: 'Choose',
  },
  printerFriendly: {
    id: 'superlink.lbl_printer_friendly_report',
    defaultMessage: 'Printer friendly report(s)',
  },
  automaticReportsOpt: {
    id: 'automatic.options',
    defaultMessage: 'Automatic report(s) option: ',
  },
});

const EditAssessmentAutoReportForm = ({ jobs, jobSearching, searchJob, activeLocales, handleCancel }) => {
  const { formatMessage } = useIntl();
  const { setFieldValue, isSubmitting } = useFormikContext();

  const { getReportsToAutomaticallyGenerate } = useCustomFunctionalityCheck();
  const reportsToAutomaticallyGenerate = getReportsToAutomaticallyGenerate();

  const printerFriendly = formatMessage(i18n.printerFriendly);
  return (
    <Form>
      <Panel header={formatMessage(i18n.automaticReportsOpt)}>
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <label className="field-label" htmlFor="emails">
              <FormattedMessage id="superlink.notificationEmail" defaultMessage="Send auto-generated report(s) to" />
              <br />
              <FormattedMessage
                id="superlink.emails.hint"
                defaultMessage="Press ENTER after each provided email"
                tagName="small"
              />
            </label>
            <Field
              id="emails"
              name="emails"
              component={FormikChips}
              setFieldValue={setFieldValue}
              validate={(e) => validateEmails(e, formatMessage)}
              addOnBlur
            />
          </div>

          <div className="p-col-12">
            <label className="field-label" htmlFor="superlink-reports">
              <FormattedMessage
                id="superlink.autogenerateReports"
                defaultMessage="Auto-generate reports (Target Job required)"
              />
            </label>
            <Field
              setFieldValue={setFieldValue}
              options={reportsToAutomaticallyGenerate}
              component={FormikMultiSelect}
              id="autogenerateReports"
              name="autogenerateReports"
              placeholder={`- ${formatMessage(i18n.choose)} -`}
            />
          </div>
          <div className="p-col-12">
            <label className="field-label" htmlFor="targetJob">
              <FormattedMessage id="attrs.targetJob" defaultMessage="Target Job Profile" />
            </label>
            <Field
              id="targetJob"
              name="targetJob"
              component={renderAsyncSelector}
              processing={jobSearching}
              data={jobs}
              filter
              valueField="id"
              textField="name"
              onSearch={searchJob}
              setFieldValue={(fieldName, selectedValue) => {
                setFieldValue(fieldName, selectedValue.id);
              }}
              placeholder={`- ${formatMessage(i18n.choose)} -`}
            />
          </div>

          <div className="p-col-12">
            <FormattedMessage id="attrs.locale" defaultMessage="Preferred Language">
              {(text) => (
                <label className="field-label" htmlFor="superlink-reportLang">
                  {text}
                </label>
              )}
            </FormattedMessage>
            <Field id="locale" name="locale" component={FormLanguageSelector} options={activeLocales} />
          </div>
          <div className="p-col-12">
            <Field
              id="printerFriendlyReport"
              name="printerFriendlyReport"
              component={FormikCheckbox}
              labelText={printerFriendly}
              setFieldValue={setFieldValue}
            />
          </div>
        </div>
        <div className="p-grid">
          <div className="p-col-12">
            <FormattedMessage id="actions.save" defaultMessage="Save">
              {(text) => (
                <Button icon={isSubmitting ? 'pi pi-check' : ''} type="submit" label={text} disabled={isSubmitting} />
              )}
            </FormattedMessage>
            &nbsp;
            <FormattedMessage id="actions.cancel" defaultMessage="Cancel">
              {(text) => (
                <Button
                  onClick={() => handleCancel()}
                  className="p-button-secondary"
                  type="button"
                  label={text}
                  disabled={isSubmitting}
                />
              )}
            </FormattedMessage>
          </div>
        </div>
      </Panel>
      <Debug />
    </Form>
  );
};

EditAssessmentAutoReportForm.propTypes = {
  jobSearching: PropTypes.bool.isRequired,
  searchJob: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  jobs: PropTypes.arrayOf(PropTypes.any),
  activeLocales: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default EditAssessmentAutoReportForm;
